import { useEffect, useRef } from 'react';
import { useNodeStore } from '@/stores/nodeStore';
import { useTaskStore } from '@/stores/taskStore';
import { useProjectStore } from '@/stores/projectStore';
import { eventBus, CanvasEvents } from '@/utils/eventBus';
import { nodeHasResult } from '@/hooks/useDataPersistence';
import { NodeType, NodeStatus, AppNode } from '@/types';
import { generateApi } from '@/services/generateApi';
import { findStrategy, GenerationContext } from '@/services/generationStrategies';

interface UseTaskRecoveryParams {
  isLoaded: boolean;
  handleAssetGenerated: (type: 'image' | 'video' | 'audio', url: string, prompt: string) => void;
}

export function useTaskRecovery({ isLoaded, handleAssetGenerated }: UseTaskRecoveryParams) {
  const resumedTasksRef = useRef<Set<string>>(new Set());
  const recoveredNodeIdsRef = useRef<Set<string>>(new Set());

  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const nodes = useNodeStore((s) => s.nodes);
  const pendingTaskCount = useTaskStore((s) => s.pendingTasks.size);
  const resumePendingTasks = useTaskStore((s) => s.resumePendingTasks);

  useEffect(() => {
    if (!isLoaded || !currentProjectId) return;

    recoveredNodeIdsRef.current.clear();

    const currentNodes = useNodeStore.getState().nodes;
    if (currentNodes.length === 0) return;

    const needsRecovery = currentNodes.some(n => n.data._needsTaskRecovery);
    const hasWorkingNode = currentNodes.some(n => n.status === NodeStatus.WORKING);
    if (!hasWorkingNode && !needsRecovery) return;

    if (pendingTaskCount > 0 && hasWorkingNode) {
      const { updateNode } = useNodeStore.getState();
      resumePendingTasks(
        (id, data, size, title, status) => updateNode(id, data, size, title, status),
        handleAssetGenerated,
        currentProjectId,
        (task, updates) => {
          if (resumedTasksRef.current.has(task.taskId)) return;
          resumedTasksRef.current.add(task.taskId);

          if (task.nodeId && task.nodeId.startsWith('agent-node-')) {
            eventBus.emit(CanvasEvents.AGENT_TASK_COMPLETED, {
              taskId: task.taskId,
              nodeId: task.nodeId,
              status: 'SUCCESS',
              updates,
            });
          }
        },
      );
    }

    if (needsRecovery) {
      const { updateNode } = useNodeStore.getState();
      const recoveryNodes = currentNodes.filter(n => n.data._needsTaskRecovery && !recoveredNodeIdsRef.current.has(n.id));

      for (const node of recoveryNodes) {
        recoveredNodeIdsRef.current.add(node.id);
        const taskId = node.data.taskId || node.data.pendingTaskId;
        if (!taskId) {
          updateNode(node.id, { error: '任务信息丢失，无法恢复', progress: undefined, activeFeature: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.IDLE);
          continue;
        }

        (async () => {
          try {
            const data = await generateApi.getTaskStatus(taskId);

            if (data.status === 'success' && data.result) {
              const resultData = data.result;
              const nodeType = node.type;
              const strategy = findStrategy(nodeType as any);
              const request: Record<string, any> = {
                modelId: node.data.model,
                prompt: node.data.prompt,
                params: {
                  aspectRatio: node.data.aspectRatio,
                  resolution: node.data.resolution,
                  imageCount: node.data.imageCount,
                },
              };

              if (strategy) {
                const mockNode: AppNode = {
                  id: node.id,
                  type: nodeType as any,
                  x: 0, y: 0,
                  title: '',
                  status: NodeStatus.WORKING,
                  data: request,
                  inputs: [],
                };

                const rawResult = {
                  taskId,
                  status: 'success',
                  results: resultData.results,
                  error: resultData.error,
                  videoMetadata: resultData.videoMetadata,
                  analysis: resultData.analysis,
                };

                const ctx: GenerationContext = {
                  projectId: currentProjectId,
                  onProgress: () => {},
                  onAssetGenerated: handleAssetGenerated,
                  onTaskCreated: () => {},
                };

                const updates = await strategy.processResult(mockNode, rawResult, ctx, request.prompt || '');
                updateNode(node.id, { ...updates, progress: undefined, activeFeature: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.SUCCESS);

                if (updates.image) handleAssetGenerated('image', updates.image, '');
                if (updates.videoUri) handleAssetGenerated('video', updates.videoUri, '');
                if (updates.audioUri) handleAssetGenerated('audio', updates.audioUri, '');

                if (node.id.startsWith('agent-node-')) {
                  eventBus.emit(CanvasEvents.AGENT_TASK_COMPLETED, {
                    taskId,
                    nodeId: node.id,
                    status: 'SUCCESS',
                    updates,
                  });
                }

                console.log(`[Recovery] Task ${taskId} recovered for node ${node.id}`);
              } else {
                updateNode(node.id, { progress: undefined, activeFeature: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.SUCCESS);
              }
            } else if (data.status === 'failed') {
              updateNode(node.id, { error: data.error || '任务失败', progress: undefined, activeFeature: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.IDLE);
            } else if (data.status === 'pending' || data.status === 'running') {
              useTaskStore.getState().addTaskWithRequest(taskId, node.id, node.type || '', {
                modelId: node.data.model,
                prompt: node.data.prompt,
                params: {
                  aspectRatio: node.data.aspectRatio,
                  resolution: node.data.resolution,
                },
              });
              updateNode(node.id, { _needsTaskRecovery: false });
            } else {
              updateNode(node.id, { error: '未知任务状态', progress: undefined, activeFeature: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.IDLE);
            }
          } catch (e: any) {
            console.error(`[Recovery] Failed to recover task for node ${node.id}:`, e);
            updateNode(node.id, { error: e.message || '恢复任务失败', progress: undefined, activeFeature: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.IDLE);
          }
        })();
      }
    }
  }, [isLoaded, currentProjectId, nodes, pendingTaskCount]);

  useEffect(() => {
    if (!currentProjectId) return;

    const processedTaskIds = new Set<string>();

    const handleTaskCompleted = async (data: { taskId: string; nodeId: string; nodeType: string; request: Record<string, any>; result: any }) => {
      if (processedTaskIds.has(data.taskId)) return;
      processedTaskIds.add(data.taskId);

      const { updateNode } = useNodeStore.getState();
      const node = useNodeStore.getState().nodeMap.get(data.nodeId);
      if (!node) return;
      if (node.status === NodeStatus.SUCCESS || nodeHasResult(node)) return;

      try {
        const strategy = findStrategy(data.nodeType as any);
        const request = data.request || {};
        const prompt = request.prompt || '';

        if (strategy && data.result) {
          const mockNode: AppNode = {
            id: data.nodeId,
            type: data.nodeType as any,
            x: 0, y: 0,
            title: '',
            status: NodeStatus.WORKING,
            data: request,
            inputs: [],
          };

          const rawResult = {
            taskId: data.taskId,
            status: 'success',
            results: data.result.results,
            error: data.result.error,
            videoMetadata: data.result.videoMetadata,
            analysis: data.result.analysis,
          };

          const ctx: GenerationContext = {
            projectId: currentProjectId,
            onProgress: () => {},
            onAssetGenerated: handleAssetGenerated,
            onTaskCreated: () => {},
          };

          const updates = await strategy.processResult(mockNode, rawResult, ctx, prompt);
          updateNode(data.nodeId, { ...updates, progress: undefined, activeFeature: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.SUCCESS);

          if (updates.image) handleAssetGenerated('image', updates.image, '');
          if (updates.videoUri) handleAssetGenerated('video', updates.videoUri, '');
          if (updates.audioUri) handleAssetGenerated('audio', updates.audioUri, '');

          console.log(`[WS-TaskCompleted] Task ${data.taskId} processed for node ${data.nodeId}`);
        }
      } catch (e) {
        console.error(`[WS-TaskCompleted] Failed to process task ${data.taskId}:`, e);
      }
    };

    eventBus.on(CanvasEvents.TASK_COMPLETED, handleTaskCompleted);
    return () => {
      eventBus.off(CanvasEvents.TASK_COMPLETED, handleTaskCompleted);
    };
  }, [currentProjectId, handleAssetGenerated]);
}
